import type { ApiErrorBody, LoginResponse, SessionUser } from '@bidmanager/shared';
import { reportIssue, setAccessTokenSource } from './log';

/**
 * Admin console API client — same shape as the client portal's
 * (`apps/web/src/api.ts`): access token in memory only, refresh token in
 * localStorage, one shared refresh flight so concurrent 401s cannot rotate the
 * same token twice. The storage key differs so a developer running both
 * portals against one browser profile never has them fighting over a session.
 */

const REFRESH_KEY = 'bidmanager-admin:refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

// So a failure reported by this console carries who hit it. Read lazily on each
// report, since the token is rotated in place.
setAccessTokenSource(() => accessToken);

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

function setSession(response: LoginResponse): void {
  accessToken = response.accessToken;
  localStorage.setItem(REFRESH_KEY, response.refreshToken);
}

export function clearSession(): void {
  accessToken = null;
  localStorage.removeItem(REFRESH_KEY);
}

export async function login(
  email: string,
  password: string,
  clientSlug?: string,
): Promise<SessionUser> {
  const response = await send('POST', '/auth/login', {
    email,
    password,
    ...(clientSlug ? { clientSlug } : {}),
  });
  const body = (await parse(response)) as LoginResponse;
  setSession(body);
  return body.user;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    await send('POST', '/auth/logout', { refreshToken }).catch(() => undefined);
  }
  clearSession();
}

/** Restore a session from the stored refresh token on page load. */
export async function restore(): Promise<SessionUser | null> {
  if (!getRefreshToken()) return null;
  const ok = await refreshSession();
  if (!ok) return null;
  try {
    const me = (await request<{ id: string; clientId: string; role: SessionUser['role'] }>(
      'GET',
      '/auth/me',
    )) as { id: string; clientId: string; role: SessionUser['role'] };
    return {
      id: me.id,
      clientId: me.clientId,
      clientName: '',
      email: '',
      username: '',
      role: me.role,
    };
  } catch {
    clearSession();
    return null;
  }
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    let response = await send(method, path, body);

    if (response.status === 401 && getRefreshToken()) {
      if (await refreshSession()) response = await send(method, path, body);
    }

    return (await parse(response)) as T;
  } catch (error) {
    reportApiFailure(method, path, error);
    throw error;
  }
}

/**
 * Report only what the operator cannot act on: the API being unreachable, and
 * 5xx. A 4xx is the server telling this console it asked wrongly — it is
 * already shown in the UI, and recording every validation failure would bury
 * the entries that matter.
 */
function reportApiFailure(method: string, path: string, error: unknown): void {
  const status = error instanceof ApiError ? error.status : 0;
  if (status !== 0 && status < 500) return;
  reportIssue('api', error, {
    code: error instanceof ApiError ? `${status} ${error.body.code}` : 'NETWORK',
    context: { method, path, status },
  });
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`/api${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  const response = await send('POST', '/auth/refresh', { refreshToken });
  if (!response.ok) {
    clearSession();
    return false;
  }
  setSession((await response.json()) as LoginResponse);
  return true;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (parsed ?? {
        code: 'VALIDATION_FAILED',
        message: `Request failed (${response.status})`,
      }) as ApiErrorBody,
    );
  }
  return parsed;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}
